import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { customersAPI, servicesAPI, invoicesAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatCurrency } from '../utils/helpers';
import { Plus, Trash, ArrowLeft, UserPlus } from '@phosphor-icons/react';

export const CreateInvoicePage = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [items, setItems] = useState([{ service_name: '', particulars: '', size: '', quantity: 0, rate: 0, total: 0 }]);
  const [advancePaid, setAdvancePaid] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState('');
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [newService, setNewService] = useState({ name: '', description: '', price: '', unit: 'piece' });

  // New customer form
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    mobile: '',
    email: '',
    address: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, servicesRes] = await Promise.all([
        customersAPI.getAll(),
        servicesAPI.getAll(),
      ]);
      setCustomers(customersRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    
    if (!newCustomer.name || !newCustomer.mobile) {
      toast.error('Name and Mobile are required');
      return;
    }

    try {
      const response = await customersAPI.create(newCustomer);
      toast.success('Customer added successfully!');
      
      // Add to customers list and select it
      setCustomers([...customers, response.data]);
      setSelectedCustomer(response.data.id);
      
      // Reset form and close dialog
      setNewCustomer({ name: '', mobile: '', email: '', address: '' });
      setCustomerDialogOpen(false);
    } catch (error) {
      toast.error('Failed to add customer');
    }
  };

  const addItem = () => {
    setItems([...items, { service_name: '', particulars: '', size: '', quantity: 0, rate: 0, total: 0 }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    if (field === 'quantity' || field === 'rate') {
      newItems[index].total = newItems[index].quantity * newItems[index].rate;
    }

    setItems(newItems);
  };

  const selectService = (index, serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      updateItem(index, 'service_name', service.name);
      updateItem(index, 'rate', service.price);
      updateItem(index, 'total', items[index].quantity * service.price);
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    if (!newService.name || !newService.price) {
      toast.error('Service name and price required');
      return;
    }
    try {
      const response = await servicesAPI.create({
        name: newService.name,
        description: newService.description,
        price: parseFloat(newService.price),
        unit: newService.unit
      });
      toast.success('Service added!');
      setServices([...services, response.data]);
      setNewService({ name: '', description: '', price: '', unit: 'piece' });
      setServiceDialogOpen(false);
    } catch (error) {
      toast.error('Failed to add service');
    }
  };

  const subtotal = items.reduce((sum, item) => sum + item.total, 0);
  const grandTotal = subtotal - discount;
  const balance = grandTotal - advancePaid;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }

    if (items.some(item => !item.service_name || item.rate <= 0)) {
      toast.error('Please fill all item details');
      return;
    }

    try {
      await invoicesAPI.create({
        customer_id: selectedCustomer,
        items: items,
        discount: parseFloat(discount),
        advance_paid: parseFloat(advancePaid),
        notes: notes,
      });
      toast.success('Invoice created successfully!');
      navigate('/invoices');
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="text-[#6B7280]">Loading...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24 md:pb-8" data-testid="create-invoice-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/invoices')} data-testid="back-button" className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" weight="bold" /> Back to Invoices
          </Button>
          <h1 className="font-['Chivo'] text-4xl md:text-5xl font-black tracking-tighter text-[#121212] mb-2">
            Create Invoice
          </h1>
          <p className="text-[#6B7280] font-medium leading-relaxed">Generate a new invoice for your customer</p>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="border-[#E5E7EB] bg-white mb-6" data-testid="customer-selection-card">
            <CardHeader>
              <CardTitle className="font-['Chivo'] text-xl font-bold tracking-tight">Customer Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="customer">Select Customer</Label>
                <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                  <SelectTrigger data-testid="customer-select">
                    <SelectValue placeholder="Choose a customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name} - {customer.mobile}
                      </SelectItem>
                    ))}
                    {/* Add New Customer Button inside dropdown */}
                    <div className="border-t border-gray-200 mt-1 pt-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCustomerDialogOpen(true);
                        }}
                        className="flex items-center gap-2 w-full px-2 py-2 text-sm text-[#FF5A36] hover:bg-orange-50 rounded-md transition-colors"
                        data-testid="add-customer-dropdown-button"
                      >
                        <UserPlus className="h-4 w-4" weight="bold" />
                        <span className="font-medium">Add New Customer</span>
                      </button>
                    </div>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Add Customer Dialog */}
          <Dialog open={customerDialogOpen} onOpenChange={setCustomerDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Customer</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddCustomer} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="customer-name">Customer Name *</Label>
                  <Input
                    id="customer-name"
                    data-testid="new-customer-name"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                    placeholder="Enter customer name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customer-mobile">Mobile Number *</Label>
                  <Input
                    id="customer-mobile"
                    data-testid="new-customer-mobile"
                    value={newCustomer.mobile}
                    onChange={(e) => setNewCustomer({ ...newCustomer, mobile: e.target.value })}
                    placeholder="9876543210"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customer-email">Email (Optional)</Label>
                  <Input
                    id="customer-email"
                    data-testid="new-customer-email"
                    type="email"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                    placeholder="customer@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customer-address">Address (Optional)</Label>
                  <Textarea
                    id="customer-address"
                    data-testid="new-customer-address"
                    value={newCustomer.address}
                    onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                    placeholder="Enter address..."
                  />
                </div>
                <Button type="submit" data-testid="save-customer-button" className="w-full bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full">
                  <UserPlus className="h-4 w-4 mr-2" weight="bold" />
                  Add Customer
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Card className="border-[#E5E7EB] bg-white mb-4" data-testid="items-card">
            <CardHeader>
              <CardTitle className="font-['Chivo'] text-xl font-bold tracking-tight">Invoice Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {items.map((item, index) => (
                  <div key={index} data-testid={`invoice-item-${index}`} className="border border-[#E5E7EB] p-4 rounded-lg">
                    {/* Row 1: Service Name */}
                    <div className="mb-3">
                      <Label htmlFor={`service-${index}`}>Service Name</Label>
                      <Select
                        value={item.service_name}
                        onValueChange={(value) => {
                          const service = services.find(s => s.name === value);
                          if (service) selectService(index, service.id);
                        }}
                      >
                        <SelectTrigger data-testid={`service-select-${index}`}>
                          <SelectValue placeholder="Select service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.name}>
                              {service.name} - {formatCurrency(service.price)}
                            </SelectItem>
                          ))}
                          {/* Add New Service Button */}
                          <div className="border-t border-gray-200 mt-1 pt-1">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setServiceDialogOpen(true);
                              }}
                              className="flex items-center gap-2 w-full px-2 py-2 text-sm text-[#FF5A36] hover:bg-orange-50 rounded-md transition-colors"
                            >
                              <Plus className="h-4 w-4" weight="bold" />
                              <span className="font-medium">+ Add New Service</span>
                            </button>
                          </div>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Row 2: Particular Details & Size */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <div>
                        <Label htmlFor={`particulars-${index}`}>Particular Details</Label>
                        <Input
                          id={`particulars-${index}`}
                          placeholder="e.g. Color: Red, Material: Vinyl"
                          value={item.particulars || ''}
                          onChange={(e) => updateItem(index, 'particulars', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`size-${index}`}>Size</Label>
                        <Input
                          id={`size-${index}`}
                          placeholder="e.g. 3x4 ft, A4, 100gsm"
                          value={item.size || ''}
                          onChange={(e) => updateItem(index, 'size', e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Row 3: Rate, Quantity */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor={`rate-${index}`}>Rate (₹)</Label>
                        <Input
                          id={`rate-${index}`}
                          data-testid={`rate-input-${index}`}
                          type="number"
                          min="0"
                          value={item.rate === 0 ? '' : item.rate}
                          onChange={(e) => {
                            const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                            updateItem(index, 'rate', val);
                          }}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`qty-${index}`}>Quantity</Label>
                        <Input
                          id={`qty-${index}`}
                          data-testid={`quantity-input-${index}`}
                          type="number"
                          min="0"
                          value={item.quantity === 0 ? '' : item.quantity}
                          onChange={(e) => {
                            const val = e.target.value === '' ? 0 : parseInt(e.target.value);
                            updateItem(index, 'quantity', val);
                          }}
                          placeholder="0"
                        />
                      </div>
                      <div className="flex items-end">
                        <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-2 w-full text-center">
                          <span className="text-xs text-gray-500 block">Total</span>
                          <span className="font-bold text-[#FF5A36]">{formatCurrency(item.total)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Delete Button */}
                    {items.length > 1 && (
                      <div className="flex justify-end mt-3">
                        <Button type="button" variant="ghost" size="sm" data-testid={`remove-item-${index}`} onClick={() => removeItem(index)}>
                          <Trash className="h-4 w-4 text-red-600 mr-1" weight="bold" /> Remove
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Add Service Dialog */}
          <Dialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Service</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddService} className="space-y-4">
                <div className="space-y-2">
                  <Label>Service Name *</Label>
                  <Input
                    value={newService.name}
                    onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                    placeholder="e.g. Flex Banner"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={newService.description}
                    onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                    placeholder="e.g. Flex printing"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Price (₹) *</Label>
                    <Input
                      type="number"
                      value={newService.price}
                      onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                      placeholder="25"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unit</Label>
                    <Input
                      value={newService.unit}
                      onChange={(e) => setNewService({ ...newService, unit: e.target.value })}
                      placeholder="sq.ft"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full">
                  <Plus className="h-4 w-4 mr-2" weight="bold" /> Add Service
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Add Item Button - Between Invoice Items and Payment Details */}
          <div className="flex justify-center mb-4">
            <Button 
              type="button" 
              data-testid="add-item-button" 
              onClick={addItem} 
              variant="outline"
              className="border-2 border-dashed border-[#FF5A36] text-[#FF5A36] hover:bg-orange-50 rounded-full px-6"
            >
              <Plus className="h-5 w-5 mr-2" weight="bold" /> Add Another Item
            </Button>
          </div>

          <Card className="border-[#E5E7EB] bg-white mb-6" data-testid="payment-details-card">
            <CardHeader>
              <CardTitle className="font-['Chivo'] text-xl font-bold tracking-tight">Payment Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-[#FAFAFA] p-4 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span className="text-[#6B7280]">Subtotal</span>
                    <span className="font-bold text-[#121212]">{formatCurrency(subtotal)}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="discount">Discount (₹)</Label>
                    <Input
                      id="discount"
                      data-testid="discount-input"
                      type="number"
                      min="0"
                      max={subtotal}
                      value={discount === 0 ? '' : discount}
                      onChange={(e) => setDiscount(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="advance">Advance Payment (₹)</Label>
                    <Input
                      id="advance"
                      data-testid="advance-input"
                      type="number"
                      min="0"
                      max={grandTotal}
                      value={advancePaid === 0 ? '' : advancePaid}
                      onChange={(e) => setAdvancePaid(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                      placeholder="0"
                    />
                  </div>
                </div>
                {discount > 0 && (
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                    <div className="flex justify-between text-sm">
                      <span className="text-green-700">Discount Applied</span>
                      <span className="font-bold text-green-700">- {formatCurrency(discount)}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-green-700">Grand Total</span>
                      <span className="font-bold text-green-800">{formatCurrency(grandTotal)}</span>
                    </div>
                  </div>
                )}
                <div className="bg-orange-50 p-4 rounded-lg border border-[#FF5A36]">
                  <div className="flex justify-between">
                    <span className="font-bold text-[#121212]">Balance Due</span>
                    <span className="font-black text-2xl text-[#FF5A36]">{formatCurrency(balance)}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="notes-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Additional notes for the customer..."
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Button type="submit" data-testid="submit-invoice-button" className="w-full bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full py-6 text-lg font-bold">
            Create Invoice
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CreateInvoicePage;